import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, MessageSquare, Plus, Trash2, Facebook as FacebookIcon,
  Instagram, Lock, ImageIcon, FileText, Link2, Bot, User, MessageCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_RULE = {
  keyword: '', match_type: 'contains',
  public_reply_enabled: false, public_reply_text: '',
  dm_enabled: false, dm_content_type: 'text', dm_content: '', resource_id: null,
  continuation_mode: 'asistente_ia', duplicate_cooldown_hours: 24, is_active: true,
};

const DM_STATUS_LABEL = {
  sent: 'Enviado', failed: 'Falló', skipped_window_expired: 'Ventana expirada (7 días)',
  skipped_duplicate_contact: 'Omitido — cooldown', skipped_rate_limited: 'Omitido — límite de tasa',
  not_applicable: 'N/A',
};
const PUBLIC_STATUS_LABEL = {
  sent: 'Enviado', failed: 'Falló', skipped_disabled: 'Desactivado', not_applicable: 'N/A',
};

export const CommentAutomationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [connections, setConnections] = useState([]);
  const [connectionId, setConnectionId] = useState(searchParams.get('connection_id') || '');
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [resources, setResources] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);

  const selectedConnection = connections.find(c => c.id === connectionId);
  const platform = selectedConnection?.platform;
  const platformDisabled = platform === 'instagram' && !selectedConnection?.instagram_enabled;

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/comment-automation/connections`);
      setConnections(res.data || []);
      if (!connectionId && res.data?.length) {
        setConnectionId(res.data[0].id);
      }
    } catch {
      toast.error('Error al cargar conexiones');
    }
  }, [connectionId]);

  const fetchPosts = useCallback(async (refresh = false) => {
    if (!connectionId) return;
    setLoadingPosts(true);
    try {
      const res = await axios.get(`${API}/integrations/comment-automation/posts`, {
        params: { connection_id: connectionId, refresh },
      });
      setPosts(res.data || []);
      if (refresh) toast.success('Publicaciones sincronizadas');
    } catch {
      toast.error('Error al cargar publicaciones');
    } finally {
      setLoadingPosts(false);
    }
  }, [connectionId]);

  const fetchRulesAndLogs = useCallback(async (post) => {
    if (!post || !connectionId) return;
    try {
      const [rulesRes, logsRes] = await Promise.all([
        axios.get(`${API}/integrations/comment-automation/rules`, {
          params: { connection_id: connectionId, social_post_id: post.id },
        }),
        axios.get(`${API}/integrations/comment-automation/logs`, {
          params: { connection_id: connectionId, social_post_id: post.id },
        }),
      ]);
      setRules(rulesRes.data || []);
      setLogs(logsRes.data || []);
    } catch {
      toast.error('Error al cargar reglas');
    }
  }, [connectionId]);

  const fetchResources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/integrations/comment-automation/resources`);
      setResources(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchConnections(); fetchResources(); }, []); // eslint-disable-line
  useEffect(() => { if (connectionId) { fetchPosts(false); setSelectedPost(null); setRules([]); setLogs([]); } }, [connectionId]); // eslint-disable-line
  useEffect(() => { if (selectedPost) fetchRulesAndLogs(selectedPost); }, [selectedPost, fetchRulesAndLogs]);

  const openNewRule = () => { setEditingRule(EMPTY_RULE); setRuleDialogOpen(true); };
  const openEditRule = (rule) => { setEditingRule({ ...rule }); setRuleDialogOpen(true); };

  const handleSaveRule = async () => {
    if (!editingRule.keyword.trim()) { toast.error('La palabra clave es obligatoria'); return; }
    if (!editingRule.public_reply_enabled && !editingRule.dm_enabled) {
      toast.error('Activa respuesta pública, DM, o ambos'); return;
    }
    setSaving(true);
    try {
      const payload = { ...editingRule, connection_id: connectionId, social_post_id: selectedPost.id };
      if (editingRule.id) {
        await axios.put(`${API}/integrations/comment-automation/rules/${editingRule.id}`, payload);
      } else {
        await axios.post(`${API}/integrations/comment-automation/rules`, payload);
      }
      toast.success('Regla guardada');
      setRuleDialogOpen(false);
      fetchRulesAndLogs(selectedPost);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar la regla');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await axios.delete(`${API}/integrations/comment-automation/rules/${ruleId}`);
      toast.success('Regla eliminada');
      fetchRulesAndLogs(selectedPost);
    } catch {
      toast.error('Error al eliminar la regla');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/integrations')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Integraciones
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <MessageSquare className="h-6 w-6" /> Comentario → DM
        </h1>
        <p className="text-sm text-muted-foreground">
          Responde automáticamente a comentarios con una palabra clave: respuesta pública, mensaje privado, o ambos.
        </p>
      </div>

      {/* Selector de conexión */}
      {connections.length > 1 && (
        <div className="max-w-sm">
          <Label className="text-sm mb-1.5 block">Cuenta</Label>
          <Select value={connectionId} onValueChange={setConnectionId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {connections.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.page_name || c.page_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {connections.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Conecta Facebook Messenger o Instagram DMs en Integraciones para configurar esta funcionalidad.
          </CardContent>
        </Card>
      )}

      {platformDisabled && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-300">
          <Lock className="h-4 w-4 shrink-0" />
          Instagram aún no está disponible: falta la aprobación de Meta de <code className="text-xs">instagram_manage_comments</code>.
          Puedes revisar las publicaciones, pero no podrás activar reglas hasta que se apruebe.
        </div>
      )}

      {connectionId && (
        <>
          {/* Grid de publicaciones */}
          <div className="flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2">
              {platform === 'instagram' ? <Instagram className="h-4 w-4" /> : <FacebookIcon className="h-4 w-4" />}
              Publicaciones
            </h2>
            <Button size="sm" variant="outline" onClick={() => fetchPosts(true)} disabled={loadingPosts}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingPosts ? 'animate-spin' : ''}`} />
              Refrescar
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {posts.map(post => (
              <Card
                key={post.id}
                className={`cursor-pointer transition-all overflow-hidden ${selectedPost?.id === post.id ? 'ring-2 ring-blue-500' : 'hover:border-blue-300'}`}
                onClick={() => setSelectedPost(post)}
              >
                <div className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                  {post.thumbnail_url ? (
                    <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <MessageSquare className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <CardContent className="p-2">
                  <p className="text-xs line-clamp-2">{post.caption || '(sin texto)'}</p>
                </CardContent>
              </Card>
            ))}
            {posts.length === 0 && !loadingPosts && (
              <p className="col-span-full text-sm text-muted-foreground py-6 text-center">
                Sin publicaciones sincronizadas — pulsa "Refrescar".
              </p>
            )}
          </div>

          {/* Reglas del post seleccionado */}
          {selectedPost && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Reglas para esta publicación</CardTitle>
                  <CardDescription className="line-clamp-1">{selectedPost.caption}</CardDescription>
                </div>
                <Button size="sm" onClick={openNewRule} disabled={platformDisabled}>
                  <Plus className="h-4 w-4 mr-2" /> Nueva regla
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {rules.length === 0 && (
                  <p className="text-sm text-muted-foreground">Sin reglas todavía.</p>
                )}
                {rules.map(rule => (
                  <div key={rule.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">{rule.keyword}</Badge>
                        <Badge variant="secondary" className="text-xs">{rule.match_type}</Badge>
                        {rule.public_reply_enabled && <Badge className="text-xs bg-blue-500">Respuesta pública</Badge>}
                        {rule.dm_enabled && <Badge className="text-xs bg-green-600">DM</Badge>}
                        {rule.dm_enabled && (
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            {rule.continuation_mode === 'asistente_ia' && <Bot className="h-3 w-3" />}
                            {rule.continuation_mode === 'humano' && <User className="h-3 w-3" />}
                            {rule.continuation_mode === 'mensaje_fijo' && <MessageCircle className="h-3 w-3" />}
                            {rule.continuation_mode}
                          </Badge>
                        )}
                        {!rule.is_active && <Badge variant="secondary" className="text-xs">Inactiva</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEditRule(rule)}>Editar</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteRule(rule.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Auditoría de disparos */}
          {selectedPost && logs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Auditoría de disparos</CardTitle>
                <CardDescription>Últimos comentarios que activaron una regla en esta publicación</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contacto</TableHead>
                      <TableHead>Respuesta pública</TableHead>
                      <TableHead>DM</TableHead>
                      <TableHead>Continuación</TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map(log => (
                      <TableRow key={log.id}>
                        <TableCell>{log.commenter_name || log.commenter_external_id}</TableCell>
                        <TableCell>{PUBLIC_STATUS_LABEL[log.public_reply_status] || log.public_reply_status}</TableCell>
                        <TableCell>{DM_STATUS_LABEL[log.dm_status] || log.dm_status}</TableCell>
                        <TableCell>{log.continuation_mode || '—'}</TableCell>
                        <TableCell>{log.lead_id ? <Badge className="bg-green-600">Sí</Badge> : '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {log.triggered_at ? new Date(log.triggered_at).toLocaleString('es-MX') : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <RuleEditorDialog
        open={ruleDialogOpen}
        onOpenChange={setRuleDialogOpen}
        rule={editingRule}
        setRule={setEditingRule}
        onSave={handleSaveRule}
        saving={saving}
        resources={resources}
        onResourceCreated={fetchResources}
        platformDisabled={platformDisabled}
      />
    </div>
  );
};

const RuleEditorDialog = ({ open, onOpenChange, rule, setRule, onSave, saving, resources, onResourceCreated, platformDisabled }) => {
  const [newResourceUrl, setNewResourceUrl] = useState('');
  const [newResourceTitle, setNewResourceTitle] = useState('');
  const [creatingResource, setCreatingResource] = useState(false);

  const set = (key, value) => setRule(prev => ({ ...prev, [key]: value }));

  const handleCreateResource = async () => {
    if (!newResourceUrl.trim()) { toast.error('Pega una URL'); return; }
    setCreatingResource(true);
    try {
      const res = await axios.post(`${API}/integrations/comment-automation/resources`, {
        type: rule.dm_content_type, title: newResourceTitle || newResourceUrl, url_or_storage_ref: newResourceUrl,
      });
      toast.success('Recurso agregado');
      setNewResourceUrl(''); setNewResourceTitle('');
      await onResourceCreated();
      set('resource_id', res.data.id);
    } catch {
      toast.error('Error al agregar el recurso');
    } finally {
      setCreatingResource(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule.id ? 'Editar regla' : 'Nueva regla'}</DialogTitle>
          <DialogDescription>Define la palabra clave y qué pasa cuando alguien la comente.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Palabra clave</Label>
              <Input value={rule.keyword} onChange={e => set('keyword', e.target.value)} placeholder="precio" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de coincidencia</Label>
              <Select value={rule.match_type} onValueChange={v => set('match_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">Contiene</SelectItem>
                  <SelectItem value="exact">Exacta</SelectItem>
                  <SelectItem value="regex">Regex</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator2 />

          {/* Respuesta pública */}
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Respuesta pública al comentario</Label>
            <Switch checked={rule.public_reply_enabled} onCheckedChange={v => set('public_reply_enabled', v)} />
          </div>
          {rule.public_reply_enabled && (
            <Textarea
              value={rule.public_reply_text || ''}
              onChange={e => set('public_reply_text', e.target.value)}
              placeholder="¡Gracias por tu interés! Te escribimos por mensaje privado 😊"
              className="text-sm"
            />
          )}

          <Separator2 />

          {/* DM privado */}
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2"><Link2 className="h-4 w-4" /> DM privado (Private Reply)</Label>
            <Switch checked={rule.dm_enabled} onCheckedChange={v => set('dm_enabled', v)} />
          </div>
          {rule.dm_enabled && (
            <div className="space-y-3 pl-2 border-l-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Tipo de contenido</Label>
                <Select value={rule.dm_content_type} onValueChange={v => set('dm_content_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texto</SelectItem>
                    <SelectItem value="link">Link</SelectItem>
                    <SelectItem value="image"><span className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> Imagen</span></SelectItem>
                    <SelectItem value="document"><span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> Documento</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(rule.dm_content_type === 'text' || rule.dm_content_type === 'link') && (
                <Textarea
                  value={rule.dm_content || ''}
                  onChange={e => set('dm_content', e.target.value)}
                  placeholder={rule.dm_content_type === 'link' ? 'Mira nuestra cotización aquí: https://...' : 'Hola, gracias por tu interés...'}
                  className="text-sm"
                />
              )}

              {(rule.dm_content_type === 'image' || rule.dm_content_type === 'document') && (
                <div className="space-y-2">
                  <Select value={rule.resource_id || ''} onValueChange={v => set('resource_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Elige un recurso guardado" /></SelectTrigger>
                    <SelectContent>
                      {resources.filter(r => r.type === rule.dm_content_type).map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.title || r.url_or_storage_ref}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Input placeholder="Título (opcional)" value={newResourceTitle} onChange={e => setNewResourceTitle(e.target.value)} className="text-xs" />
                    <Input placeholder="URL pública del archivo" value={newResourceUrl} onChange={e => setNewResourceUrl(e.target.value)} className="text-xs" />
                    <Button size="sm" variant="outline" onClick={handleCreateResource} disabled={creatingResource}>
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">La URL debe ser pública (ej. un link de Drive compartido) — Meta la descarga al enviar el DM.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Al responder el DM</Label>
                  <Select value={rule.continuation_mode} onValueChange={v => set('continuation_mode', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asistente_ia">Asistente IA toma la conversación</SelectItem>
                      <SelectItem value="mensaje_fijo">Solo el DM, sin seguimiento</SelectItem>
                      <SelectItem value="humano">Queda para respuesta manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label className="text-sm cursor-help underline decoration-dotted">Cooldown (horas)</Label>
                      </TooltipTrigger>
                      <TooltipContent>Horas antes de volver a mandarle un DM al mismo contacto si vuelve a comentar la palabra clave. 0 = sin límite.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Input
                    type="number" min={0}
                    value={rule.duplicate_cooldown_hours}
                    onChange={e => set('duplicate_cooldown_hours', parseInt(e.target.value || '0', 10))}
                  />
                </div>
              </div>
            </div>
          )}

          <Separator2 />

          <div className="flex items-center justify-between">
            <Label>Regla activa</Label>
            <Switch checked={rule.is_active} onCheckedChange={v => set('is_active', v)} disabled={platformDisabled} />
          </div>
          {platformDisabled && (
            <p className="text-xs text-amber-600">Instagram aún no puede activarse — falta la aprobación de Meta.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSave} disabled={saving}>{saving ? 'Guardando…' : 'Guardar regla'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Separator2 = () => <div className="h-px bg-border" />;
